type MaybePromise<T> = T | Promise<T>;

type ChatLiveUpdatesControllerOptions = {
  intervalMs: number;
  onTick: () => void;
  sync: () => MaybePromise<void>;
};

type SyncChatMessagesOptions = {
  hasMessages: boolean;
  shouldStickToBottom: boolean;
  fetchChatList: () => Promise<void>;
  fetchLatestChatMessages: () => Promise<void>;
  defer: (callback: () => void) => void;
  scrollToBottom: () => void;
};

export class ChatLiveUpdatesController {
  private readonly intervalMs: number;
  private readonly onTick: () => void;
  private readonly sync: () => MaybePromise<void>;
  private timerId: number | null = null;

  constructor(options: ChatLiveUpdatesControllerOptions) {
    this.intervalMs = options.intervalMs;
    this.onTick = options.onTick;
    this.sync = options.sync;
  }

  public start() {
    if (this.timerId !== null) return;

    this.timerId = window.setInterval(() => {
      this.trigger();
    }, this.intervalMs);
  }

  public stop() {
    if (this.timerId === null) return;

    clearInterval(this.timerId);
    this.timerId = null;
  }

  public trigger() {
    this.onTick();
    void Promise.resolve(this.sync());
  }
}

export async function syncChatMessages(options: SyncChatMessagesOptions): Promise<void> {
  const {
    hasMessages,
    shouldStickToBottom,
    fetchChatList,
    fetchLatestChatMessages,
    defer,
    scrollToBottom,
  } = options;

  try {
    if (hasMessages) {
      await fetchLatestChatMessages();
    } else {
      await fetchChatList();
    }

    if (shouldStickToBottom || !hasMessages) {
      defer(() => scrollToBottom());
    }
  } catch (e) {
    // Polling is a fallback for missed notifications, so transient errors can be ignored here.
  }
}
